import { memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GiveawayStatus } from '@daredrop-com/rewards-api-contract';
import { giveawayEndpointIds } from 'root/src/shared/giveaways/giveaways.constants';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useDareDropApi } from 'root/src/client/v2/common/hooks/useDareDropApi';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { getReadableError } from 'root/src/client/v2/common/helpers/getReadableError';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { GiveawayTimer } from 'root/src/client/v2/giveaways/features/GiveawayHistory/components/GiveawayTimer';
import { useCreatorGiveawayEntry } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveawayEntry';
import { RedesignGiveawayVerificationModal } from 'root/src/client/v2/_pages/creator/redesign/RedesignGiveawayVerificationModal';
const TERMS_STALE_TIME_MS = 1000 * 30;
// Viewer-facing "Enter Giveaway" CTA for the creator profile reward section. Mirrors the legacy
// `ViewerGiveawaySection` logic (timer + enter / entered / no-giveaway states) but rebuilt on tRPC / the
// v2 fetcher. Direct enter when the viewer is already verified and has accepted terms; otherwise opens
// the redesign verification modal (Steam / SMS + terms). Unauthenticated viewers get a sign-in toast
// (the legacy sign-in modal is intentionally not ported here).
export const CreatorEnterGiveawayCta = memo(({ giveaway, creatorUserId }) => {
    var _a;
    const isAuthenticated = useIsAuthenticated();
    const { fetcher } = useDareDropApi();
    const { show } = useRedesignModal();
    const { enterGiveaway, isEntered, isVerified, isOwnGiveaway } = useCreatorGiveawayEntry({ giveaway, creatorUserId });
    const { loading, success, error, run } = useAsyncAction();
    const termsQuery = useQuery({
        queryKey: ['creator-giveaway-terms'],
        queryFn: () => fetcher({ endpointId: giveawayEndpointIds.GET_USER_GIVEAWAY_TERMS }, true),
        enabled: isAuthenticated,
        staleTime: TERMS_STALE_TIME_MS,
    });
    const hasAcceptedTerms = Boolean(termsQuery.data);
    const termsRequired = isAuthenticated && !hasAcceptedTerms;
    const isActive = (giveaway === null || giveaway === void 0 ? void 0 : giveaway.status) === GiveawayStatus.Active;
    const isPickingWinner = isActive && Boolean(giveaway === null || giveaway === void 0 ? void 0 : giveaway.endDate) && new Date((_a = giveaway === null || giveaway === void 0 ? void 0 : giveaway.endDate) !== null && _a !== void 0 ? _a : 0) <= new Date();
    // Own giveaway: the creator manages it from the giveaway panel, no viewer CTA here.
    if (isOwnGiveaway) {
        return null;
    }
    if (!giveaway || !isActive) {
        return (<p className="text-sm text-muted-foreground">This creator has no active giveaway right now.</p>);
    }
    if (isEntered) {
        return (<div className="flex flex-col items-center gap-3">
				<GiveawayTimer giveaway={giveaway}/>
				<span className="rounded-xl border border-solid border-neon-green/30 bg-neon-green/10 px-4 py-2 text-sm font-semibold text-neon-green">
					You are entered
				</span>
			</div>);
    }
    const onEnterClick = () => {
        if (!isAuthenticated) {
            toast('Sign in to enter this giveaway.');
            return;
        }
        // Already verified this giveaway and terms accepted: enter directly.
        if (isVerified && hasAcceptedTerms) {
            void run(enterGiveaway, {
                toast: {
                    loading: 'Entering giveaway...',
                    success: 'Successfully entered the giveaway.',
                    error: caught => getReadableError(caught, 'Failed to enter the giveaway.'),
                },
            });
            return;
        }
        show(<RedesignGiveawayVerificationModal giveaway={giveaway} creatorUserId={creatorUserId} termsRequired={termsRequired}/>);
    };
    return (<div className="flex flex-col items-center gap-3">
			<GiveawayTimer giveaway={giveaway}/>
			<Button type="button" variant="brand" loading={loading} success={success} error={error} disabled={isPickingWinner} onClick={onEnterClick} className="min-w-52 rounded-xl text-sm font-semibold uppercase tracking-wide">
				Enter giveaway
			</Button>
		</div>);
});
